import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as XLSX from 'xlsx';
import { KTSVG, toAbsoluteUrl } from '../../../_metronic/helpers';
import loadingimage from "../../../Assets/loading-buffering1.gif"
import CustomDateCalendar from './CustomDateCalendar';


interface ReportData {
    id: number;
    booking_id: string;
    total_amount: string;
    paid_date: string;
    total_dailycollection_amount: string;
    journey_id: string;
    split_id: string;
    created_at: string;
}

interface ApiResponse {
    status: string;
    data: ReportData[];
}

const DailyReport: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [reportData, setReportData] = useState<ReportData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(12); // Change as per your requirement
    const [selectedExpenses, setSelectedExpenses] = useState<ReportData[]>([]);
    const [isMasterChecked, setIsMasterChecked] = useState<boolean>(false);
      const [progress, setProgress] = React.useState<number>(0);
      const [showProgressBar, setShowProgressBar] = React.useState<boolean>(false);

    // Handle date change
    const handleDateChange = (date: Date | null) => {
        if (date) {
            setSelectedDate(date);
            console.log("Selected date changed:", date);
        }
    };

    // Fetch report data from the API
    const fetchData = async (date: Date) => {
        setLoading(true);
        setError('');
        setReportData([]); // 🔁 Clear old data before fetching

        const formattedDate = date.toLocaleDateString('en-GB').replace(/\//g, '-');

        try {
            const response = await fetch(
                'https://backendcway.causewaycargo.com/superadmin/dailypldata',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do',
                    },
                    body: JSON.stringify({ date: formattedDate }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result: ApiResponse = await response.json();

            if (result.status === 'success') {
                if (result.data && result.data.length > 0) {
                    setReportData(result.data);
                    console.log("daily Report", result.data);
                } else {
                    setReportData([]); // 🔁 Explicitly clear if no data
                    setError('No data available for this date');
                }
            } else {
                setReportData([]); // 🔁 Clear on failure
                setError('Failed to fetch data');
            }
        } catch (err) {
            setReportData([]); // 🔁 Clear on error
            setError('An error occurred while fetching data');
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    };


    // Fetch data whenever selected date changes
    useEffect(() => {
        console.log("Fetching data for:", selectedDate);
        fetchData(selectedDate);
    }, [selectedDate]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentReportData = reportData.slice(startIndex, startIndex + itemsPerPage);

    const totalPages = Math.ceil(reportData.length / itemsPerPage);

    // Excel Export function
    const handleExportToExcel = () => {
        setShowProgressBar(true); // Show progress bar
        setProgress(0); // Reset progress to 0%
    
        const startTime = Date.now();
        const estimatedTime = 5000; // You can adjust based on real-time if needed
    
        const intervalId = setInterval(() => {
            const elapsedTime = Date.now() - startTime;
            const progressPercentage = Math.min((elapsedTime / estimatedTime) * 100, 100);
            setProgress(progressPercentage);
    
            if (progressPercentage >= 100) {
                clearInterval(intervalId);
            }
        }, 100); // Update every 100ms
    
        // Proceed to export logic after estimated time
        setTimeout(() => {
            const wb = XLSX.utils.book_new();
    
            const formatDate = (isoString: string) => {
                if (!isoString) return "NA";
                const date = new Date(isoString);
                return date.toLocaleDateString("en-GB").replace(/\//g, "-"); // "DD-MM-YYYY"
            };
    
            const modifiedReportData = reportData.map(({ total_dailycollection_amount, paid_date, created_at, booking_id, split_id, ...rest }) => [
                rest.journey_id || "NA",
                booking_id ? booking_id : split_id,
                rest.total_amount || "NA",
                formatDate(paid_date),
                formatDate(created_at),
            ]);
    
            let ws = XLSX.utils.aoa_to_sheet([
                ["Container ID", "Booking ID", "Total Amount", "Paid Date", "Created At"],
                ...modifiedReportData,
                [""],
                ["", "Total", reportData.length > 0 ? reportData[0].total_dailycollection_amount : "0", "", ""]
            ]);
    
            ws["!cols"] = [
                { wch: 15 },
                { wch: 20 },
                { wch: 15 },
                { wch: 12 },
                { wch: 12 }
            ];
    
            ws["!ref"] = XLSX.utils.encode_range({
                s: { r: 0, c: 0 },
                e: { r: modifiedReportData.length + 2, c: 4 },
            });
    
            XLSX.utils.book_append_sheet(wb, ws, "Report Data");
            XLSX.writeFile(wb, "DailyCollectionReport.xlsx");
    
            setProgress(100); // Forcefully set progress to 100% at end
            setShowProgressBar(false); // Hide the progress bar
        }, estimatedTime); // Match with estimated time
    };
    


    const handleCheckboxChange = (expense: ReportData, isChecked: boolean) => {
        const updatedExpenses = isChecked
            ? [...selectedExpenses, expense]
            : selectedExpenses.filter(e =>
                e.booking_id
                    ? e.booking_id !== expense.booking_id  // If booking_id exists, filter by it
                    : e.split_id !== expense.split_id      // If booking_id is null, filter by split_id
            );

        setSelectedExpenses(updatedExpenses);

        // Check if all rows in the current page are selected
        setIsMasterChecked(updatedExpenses.length === currentReportData.length);
    };

    const handleMasterCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        setIsMasterChecked(isChecked);

        // Select all data when master checkbox is checked
        const updatedSelectedExpenses = isChecked ? [...reportData] : [];
        setSelectedExpenses(updatedSelectedExpenses);
    };
    const [showCalendar, setShowCalendar] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowCalendar(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="card px-lg-3">
            {showProgressBar && (
        <>
          <div className="modal-backdrop shadow-lg rounded-5">
            <div className="modal d-block" tabIndex={-1} role="dialog">
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content pb-10 pt-4">
                  <div className="modal-header py-2 d-flex justify-content-end">
                    <div className="modal-body">
                      <h5 className="mb-4">Exporting Data...</h5>
                      <div className="progress" style={{ height: "20px" }}>
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                          role="progressbar"
                          style={{ width: `${progress}%` }}
                          aria-valuenow={progress}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          {Math.round(progress)}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
            <div className='card-header border-0 px-4 pt-5 mt-3'>
                {/* Date Picker */}
                <div className="d-flex flex-column flex-sm-row align-items-center gap-4">
                    <div className="position-relative d-inline-block" ref={wrapperRef}>
                        <input
                            className="form-control pe-5"
                            value={selectedDate ? selectedDate.toLocaleDateString('en-GB') : ""}
                            readOnly
                            onClick={() => setShowCalendar((prev) => !prev)}
                            style={{ cursor: 'pointer', width: '250px' }}
                        />
                        <img
                            src={toAbsoluteUrl('/media/icons/duotune/general/gen014.svg')}
                            alt="calendar-icon"
                            width={20}
                            className="position-absolute"
                            style={{
                                top: '50%',
                                right: '10px',
                                transform: 'translateY(-50%)',
                                pointerEvents: 'none',
                            }}
                        />

                        {/* Calendar dropdown */}
                        {showCalendar && (
                            <div
                                className="position-absolute bg-white shadow rounded"
                                style={{
                                    top: 'calc(100% + 4px)',
                                    zIndex: 1000,
                                    minWidth: '280px',
                                }}
                            >
                                <CustomDateCalendar
                                    selectedDate={selectedDate}
                                    onDateChange={(date) => {
                                        setSelectedDate(date);
                                        setShowCalendar(false);
                                    }}
                                    onClose={() => setShowCalendar(false)}
                                />
                            </div>
                        )}
                    </div>

                    <div className="px-4 py-3 gap-3 d-flex btn bg-warning justify-content-center align-items-center w-100 w-sm-auto">
                        Daily Collection Amount: QAR{' '}
                        {reportData[0]?.total_dailycollection_amount !== undefined
                            ? new Intl.NumberFormat().format(Math.round(parseFloat(reportData[0].total_dailycollection_amount)))
                            : 0}
                    </div>
                </div>


                <div>
                    {/* Export Button */}
                    <button
                        className="btn btn-danger py-2 px-6 mt-3"
                        onClick={handleExportToExcel}
                        disabled={selectedExpenses.length === 0}
                    >
                        Export to Excel
                    </button>
                </div>
            </div>

            <div className='card-body py-3'>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <img src={loadingimage} alt="Loading..." className="w-20" />
                    </div>
                ) : (
                    <div className='table-responsive mt-3'>
                        <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                            <thead>
                                <tr className='fw-bolder text-muted'>
                                    <th className="w-25px">
                                        <input
                                            type="checkbox"
                                            checked={isMasterChecked}
                                            onChange={handleMasterCheckboxChange}
                                            className="form-check-input"
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </th>
                                    <th className='min-w-120px'>Booking ID</th>
                                    <th className='min-w-120px'>Shipment ID</th>
                                    <th className='min-w-120px'>Paid Date</th>
                                    <th className='min-w-120px'>Total Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentReportData.length > 0 ? (
                                    currentReportData.map((data, index) => (
                                        <tr key={index}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedExpenses.some(expense =>
                                                        expense.booking_id
                                                            ? expense.booking_id === data.booking_id  // Match booking_id if it exists
                                                            : expense.split_id === data.split_id      // Otherwise, match split_id
                                                    )}
                                                    onChange={(e) => handleCheckboxChange(data, e.target.checked)}
                                                    className="form-check-input"
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </td>
                                            <td className='text-dark fw-bolder text-hover-primary'>{data.booking_id ? data.booking_id : data.split_id}</td>
                                            <td>{data.journey_id || 'N/A'}</td>
                                            <td>
                                                {(() => {
                                                    let date = new Date(data.paid_date);
                                                    let istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000)); // Convert to IST

                                                    let day = String(istDate.getDate()).padStart(2, "0");
                                                    let month = String(istDate.getMonth() + 1).padStart(2, "0"); // Months are 0-based
                                                    let year = istDate.getFullYear();

                                                    return `${day}-${month}-${year}`;
                                                })()}
                                            </td>



                                            <td >{parseFloat(data.total_amount).toFixed(2)} QAR</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="text-center py-4 text-gray-500">
                                            🚫 No reports available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        {/* Pagination */}
                        <div className="d-flex justify-content-center align-items-center mt-6 mb-3  ">
                            {reportData.length > itemsPerPage && (
                                <div className="row justify-content-center mt-3 w-100">
                                    {/* Previous Button */}
                                    <div className="col-auto">
                                        <button
                                            className="btn btn-light-primary px-3 py-2"
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                        >
                                            <KTSVG path="/media/icons/duotune/arrows/arr079.svg" className="svg-icon-3" />
                                        </button>
                                    </div>

                                    {/* Page Numbers */}
                                    <div className="col-auto d-flex flex-wrap justify-content-center gap-2">
                                        {Array.from({ length: totalPages }, (_, i) => {
                                            const pageNumber = i + 1;

                                            if (
                                                pageNumber === 1 ||
                                                pageNumber === totalPages ||
                                                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                            ) {
                                                return (
                                                    <button
                                                        key={pageNumber}
                                                        className={` btn btn-light-primary mx-1 px-4 py-2  ${currentPage === pageNumber ? 'active' : ''}`}
                                                        onClick={() => handlePageChange(pageNumber)}
                                                    >
                                                        {pageNumber}
                                                    </button>
                                                );
                                            }

                                            if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                                                return (
                                                    <span key={pageNumber} className="text-muted">
                                                        ...
                                                    </span>
                                                );
                                            }

                                            return null;
                                        })}
                                    </div>

                                    {/* Next Button */}
                                    <div className="col-auto">
                                        <button
                                            className="btn btn-light-primary px-3 py-2"
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                        >
                                            <KTSVG path="/media/icons/duotune/arrows/arr080.svg" className="svg-icon-3" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                )}
            </div>




        </div>
    );
};

export default DailyReport;